import Link from 'next/link'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { FeedTileResolver } from '../../../components/Feed/FeedTileResolver'
import { REGULAR_POST_TYPE } from '../../../constants/FeedConstants'

const SectionWrapper = styled.section`
  width: 100%;
  padding: 80px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 40px 0;
    gap: 20px;
  }
`

const TextContent = styled.div`
  max-width: 1200px;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const SectionTitle = styled.h2`
  color: black;
  font-size: 48px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  margin: 0 0 16px 0;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 12px 0;
  }
`

const SectionSubtitle = styled.p`
  color: #3f3f3f;
  font-size: 20px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  margin: 0;
  max-width: 900px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const GalleryWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 0 30px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(2, auto);
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    gap: 15px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(5, 200px);
    gap: 10px;
    padding: 0 20px;
    width: max-content;
  }
`

const GalleryItem = styled.div`
  width: 100%;
  background: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-shrink: 0;
    width: 200px;
  }
`

const FloatingArrowWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 91px;
  height: 91px;

  @media (max-width: 768px) {
    right: 5px;
    width: 70px;
    height: 70px;
  }
`

const FloatingArrowLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }

  svg {
    width: 91px;
    height: 91px;

    @media (max-width: 768px) {
      width: 70px;
      height: 70px;
    }
  }
`

const ActionButton = styled.a`
  width: 322px;
  height: 50px;
  background: linear-gradient(270deg, #ff996e 0%, #ff5565 37%, #ff4576 75%, #ff5e84 100%);
  border: none;
  border-radius: 100px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: white;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 85, 101, 0.3);
  }

  @media (max-width: 768px) {
    width: 249px;
    height: 42px;
    font-size: 14px;
  }
`

const DesignIdeasSection = ({ inspirations = [] }) => {
  const { likes: likesObj } = useSelector(state => state.like)
  const { savedIds } = useSelector(state => state.saveInspiration)

  // Convert inspiration to tile data format expected by FeedTileResolver
  const prepareTileData = (post, index) => {
    const design = post
    if (!design) return null

    return {
      key: design.objectId || index,
      objectId: design.objectId,
      id: design.objectId,
      type: design.type || 'design',
      tileType: REGULAR_POST_TYPE,
      image: design.image,
      designThumbnail: design.designThumbnail,
      urlSlug: design.urlSlug,
      altText: design.altText,
      aiTitle: design.aiTitle,
      user: design.user,
      numLikes: design.numLikes,
      createdAt: design.createdAt,
      sourcePost: design,
      likes: design.numLikes,
      ...design
    }
  }

  const renderPost = (post, index) => {
    const tileData = prepareTileData(post, index)
    if (!tileData) return <GalleryItem key={index} />

    return (
      <GalleryItem key={tileData.objectId || index}>
        <FeedTileResolver
          priority={index < 5}
          tile={tileData}
          likesObj={likesObj}
          savedList={savedIds}
          location="home"
        />
      </GalleryItem>
    )
  }

  return (
    <SectionWrapper>
      <TextContent>
        <SectionTitle>Explore Interior Design Ideas from 21M+ Creations.</SectionTitle>
        <SectionSubtitle>
          Browse millions of real room designs created by our global community. Discover styles,
          layouts, colors, and interior ideas you can bring into your own home.
        </SectionSubtitle>
      </TextContent>

      <GalleryWrapper>
        <GalleryGrid>
          {inspirations.length > 0
            ? inspirations.slice(0, 10).map((post, index) => renderPost(post, index))
            : [...Array(10)].map((_, index) => <GalleryItem key={index} />)}
        </GalleryGrid>

        <FloatingArrowWrapper>
          <Link href="/explore" passHref legacyBehavior>
            <FloatingArrowLink aria-label="View more designs">
              <svg viewBox="0 0 91 91" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_arrow)">
                  <circle cx="45.5" cy="44.5" r="32" fill="white" stroke="url(#paint0_arrow)" strokeWidth="2" />
                  <path d="M55.7375 45.7374C56.421 45.054 56.421 43.946 55.7375 43.2626L44.6006 32.1256C43.9172 31.4422 42.8091 31.4422 42.1257 32.1256C41.4423 32.809 41.4423 33.9171 42.1257 34.6005L52.0252 44.5L42.1257 54.3995C41.4423 55.0829 41.4423 56.191 42.1257 56.8744C42.8091 57.5578 43.9172 57.5578 44.6006 56.8744L55.7375 45.7374ZM54.5 44.5V46.25H54.5001V44.5V42.75H54.5V44.5Z" fill="black" />
                </g>
                <defs>
                  <filter id="filter0_arrow" x="0" y="0" width="91" height="91" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="6.25" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.29 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                  </filter>
                  <linearGradient id="paint0_arrow" x1="12.5" y1="44.5" x2="78.5" y2="44.5" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FF5E6D" />
                    <stop offset="1" stopColor="#F48D62" />
                  </linearGradient>
                </defs>
              </svg>
            </FloatingArrowLink>
          </Link>
        </FloatingArrowWrapper>
      </GalleryWrapper>

      <Link href="/explore" passHref legacyBehavior>
        <ActionButton>Explore Design Ideas</ActionButton>
      </Link>
    </SectionWrapper>
  )
}

export default DesignIdeasSection
